import React, { useEffect, useState, useMemo } from "react";
import HRNavbar from "../../../HR/HRNavbar";
import Footer from "../../../../Footer";
import Closureformbuild from "./Closureformbuild";
import CreatePackage from "./CreatePackage";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  Card,
  CardContent,
  TablePagination,
  Switch,
  IconButton,
  Tooltip,
  Stack,
  Button,
  CircularProgress,
  Modal,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/system";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkIcon from "@mui/icons-material/Link";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { API_URL } from "../../../Config/api";

// API Config

// ================= STYLES =================
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", md: 800 },
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  p: 4,
};

const OngoingServiceCard = styled(Card)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "10px",
  backgroundColor: "white",
  boxShadow: "4px 4px 10px 7px rgba(135, 135, 135, 0.05)",
  height: "3.5rem",
  borderRadius: "10px",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: "#F7F7F7",
    borderLeft: "4px solid #A5D6A7",
    borderRight: "4px solid #A5D6A7",
    transform: "translateY(-2px)",
  },
});

// ================= ACCORDION COMPONENT FOR PREVIEW & ASSIGN =================
const ServiceFormPreview = ({
  packageData,
  activeClosureForms, // Active forms for dropdown
  handleAssignFormSubmit,
  handleUnassignForm,
}) => {
  return (
    <Box>
      {packageData?.service_details?.length > 0 ? (
        packageData.service_details.map((srv, index) => {
          const serviceName = srv.service_name || `Service ID: ${srv.service}`;
          const isFormAssigned = srv.form_id != null; // Check if form is assigned

          return (
            <Accordion
              key={index}
              sx={{
                mb: 2,
                border: "1px solid #E0E0E0",
                borderRadius: "8px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: "100%", pr: 2 }}
                >
                  <Typography fontWeight={600} color="#347B89">
                    {serviceName}
                  </Typography>
                  {/* Admin visual tag for assigned form */}
                  {isFormAssigned ? (
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: "#e8f5e9",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        color: "success.main",
                        fontWeight: 600,
                      }}
                    >
                      {srv.closure_form_name}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: "#fff3e0",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        color: "warning.main",
                        fontWeight: 600,
                      }}
                    >
                      Not Assigned
                    </Typography>
                  )}
                </Stack>
              </AccordionSummary>

              <AccordionDetails
                sx={{ bgcolor: "#FAFAFA", borderTop: "1px solid #E0E0E0" }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Duration: {srv.duration} Days
                  </Typography>

                  <Stack direction="row" spacing={2} alignItems="center">
                    {isFormAssigned ? (
                      <>
                        <Typography
                          variant="body2"
                          color="success.main"
                          fontWeight={600}
                        >
                          ● Form Linked
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            handleUnassignForm(
                              packageData.package_id,
                              srv.service,
                              srv.sub_service,
                            )
                          }
                        >
                          Unassign
                        </Button>
                      </>
                    ) : (
                      <>
                        <TextField
                          select
                          size="small"
                          label="Select Active Form"
                          sx={{ minWidth: 200, bgcolor: "#fff" }}
                          defaultValue=""
                          onChange={(e) =>
                            (srv.selected_temp_form = e.target.value)
                          }
                        >
                          <MenuItem value="" disabled>
                            <em>-- Select a Form --</em>
                          </MenuItem>
                          {activeClosureForms.map((form) => (
                            <MenuItem key={form.form_id} value={form.form_id}>
                              {form.form_name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ bgcolor: "#347B89" }}
                          onClick={() =>
                            handleAssignFormSubmit(
                              packageData.package_id,
                              srv.service,
                              srv.sub_service,
                              srv.selected_temp_form,
                            )
                          }
                        >
                          Assign
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })
      ) : (
        <Typography color="error">
          No services mapped to this package.
        </Typography>
      )}
    </Box>
  );
};

// ================= MAIN COMPONENT =================
const PackageList = () => {
  const accessToken = localStorage.getItem("token");
  const [view, setView] = useState("packages");

  // State Management
  const [packages, setPackages] = useState([]);
  const [allClosureForms, setAllClosureForms] = useState([]);
  const [activeClosureForms, setActiveClosureForms] = useState([]);
  const [previewPackages, setPreviewPackages] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [subServicesMap, setSubServicesMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [searchQuery, setSearchQuery] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    autoHideDuration: 3000,
    followUpMessage: "",
  });
  const showSnackbar = (msg, sev = "success", followUpMessage = "") =>
    setSnackbar({
      open: true,
      message: msg,
      severity: sev,
      autoHideDuration: 3000,
      followUpMessage,
    });

  const handleSnackbarClose = () => {
    if (snackbar.followUpMessage) {
      setSnackbar({
        open: true,
        message: snackbar.followUpMessage,
        severity: "error",
        autoHideDuration: 5000,
        followUpMessage: "",
      });
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Modals & Form States
  const [openPackageModal, setOpenPackageModal] = useState(false);
  const [openClosureModal, setOpenClosureModal] = useState(false);
  const [openAssignPreviewModal, setOpenAssignPreviewModal] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editPackageId, setEditPackageId] = useState(null);
  const [selectedPreviewPackage, setSelectedPreviewPackage] = useState(null);

  const [packageData, setPackageData] = useState({
    package_name: "",
    package_cost: "",
    duration: "",
  });
  const [serviceDetails, setServiceDetails] = useState([
    { service_id: "", sub_service_id: "", duration: "" },
  ]);

  const [closureData, setClosureData] = useState({ formName: "", shift: "" });

  const [questions, setQuestions] = useState([
    {
      Question: "",
      type: 1,
      type_options: [{ value: "", nestedQuestion: null }],
      key: "",
    },
  ]);

  // -------------------------------------------------------------
  // INITIAL API LOAD (Common data like Services)
  // -------------------------------------------------------------
  useEffect(() => {
    fetchServicesList();
  }, []);

  // -------------------------------------------------------------
  // REFRESH DATA DYNAMICALLY ON TAB CHANGE
  // -------------------------------------------------------------
  useEffect(() => {
    if (view === "packages") {
      fetchPackages();
    } else if (view === "closure") {
      fetchAllClosureForms();
    } else if (view === "preview") {
      fetchPreviewPackages();
      fetchActiveClosureForms();
    }
    setPage(0);
    setSearchQuery("");
  }, [view]);

  // -------------------------------------------------------------
  // API FETCH FUNCTIONS
  // -------------------------------------------------------------
  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/packages/packagelist/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setPackages(response.data?.data || response.data || []);
    } catch (error) {
      showSnackbar("Failed to load packages.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviewPackages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/packages/packageformlist/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setPreviewPackages(response.data?.data || response.data || []);
    } catch (error) {
      showSnackbar("Failed to load preview list.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllClosureForms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/packages/FetchAllClosureForms/`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      setAllClosureForms(response.data?.data || response.data || []);
    } catch (error) {
      showSnackbar("Failed to load closure forms.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveClosureForms = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/packages/FetchAllActiveforms/`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      setActiveClosureForms(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Error loading active forms:", error);
    }
  };

  const fetchServicesList = async () => {
    try {
      const res = await axios.get(`${API_URL}/web/agg_hhc_services_api`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setServicesList(res.data?.data || res.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSubServices = async (serviceId) => {
    if (!serviceId || subServicesMap[serviceId]) return;
    try {
      const res = await axios.get(
        `${API_URL}/web/agg_hhc_sub_services_api/${serviceId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      setSubServicesMap((prev) => ({
        ...prev,
        [serviceId]: res.data?.data || res.data || [],
      }));
    } catch (error) {
      console.error(error);
    }
  };

  // -------------------------------------------------------------
  // STATUS TOGGLES (PUT API)
  // -------------------------------------------------------------
  const handleTogglePackageStatus = async (packageId) => {
    try {
      const response = await axios.put(
        `${API_URL}/packages/cancel/${packageId}`,
        null,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      showSnackbar(
        response.data?.message || "Package status updated!",
        "success",
      );
      fetchPackages();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to update package status",
        "error",
      );
    }
  };

  const handleToggleClosureStatus = async (formNo) => {
    try {
      const response = await axios.patch(
        `${API_URL}/packages/ClosureFormStatusChange/?form_no=${formNo}`,
        null,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      showSnackbar(
        response.data?.message || "Closure form status updated!",
        "success",
      );
      fetchAllClosureForms();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to update form status",
        "error",
      );
    }
  };

  // -------------------------------------------------------------
  // PACKAGE BUILDER HANDLERS
  // -------------------------------------------------------------
  const handlePackageChange = (e) =>
    setPackageData({ ...packageData, [e.target.name]: e.target.value });

  const handleServiceChange = (index, e) => {
    const { name, value } = e.target;
    const updatedServices = [...serviceDetails];
    updatedServices[index][name] = value;
    if (name === "service_id") {
      updatedServices[index].sub_service_id = "";
      fetchSubServices(value);
    }
    setServiceDetails(updatedServices);
  };

  const addServiceField = () =>
    setServiceDetails([
      ...serviceDetails,
      { service_id: "", sub_service_id: "", duration: "" },
    ]);
  const removeServiceField = (index) =>
    setServiceDetails(serviceDetails.filter((_, i) => i !== index));

  const resetForm = () => {
    setPackageData({ package_name: "", package_cost: "", duration: "" });
    setServiceDetails([{ service_id: "", sub_service_id: "", duration: "" }]);
    setIsEditMode(false);
    setEditPackageId(null);
    setOpenPackageModal(false);
  };

  const handleModalClose = () => resetForm();

  const handleEditClick = (pkg) => {
    setIsEditMode(true);
    setEditPackageId(pkg.package_id);

    setPackageData({
      package_name: pkg.package_name,
      package_cost: pkg.package_cost,
      duration: pkg.duration,
    });

    // ==========================================
    // FIX APPLIED HERE: Fetch Sub-services Immediately
    // ==========================================
    const updatedServiceDetails = pkg.service_details.map((s) => {
      // Fetch sub-services for this specific service ID directly
      fetchSubServices(s.service);

      return {
        id: s.id,
        // Convert both IDs to strings so MUI Select matches values correctly
        service_id: String(s.service),
        sub_service_id: String(s.sub_service),
        duration: s.duration,
      };
    });

    setServiceDetails(updatedServiceDetails);
    setOpenPackageModal(true);
  };

  const handleSavePackage = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (isEditMode) {
        const editPayload = {
          package_name: packageData.package_name,
          package_cost: Number(packageData.package_cost),
          duration: Number(packageData.duration),
          service_details: serviceDetails.map((s) => ({
            id: s.id || null,
            service: Number(s.service_id),
            sub_service: Number(s.sub_service_id),
            duration: Number(s.duration),
          })),
        };
        await axios.put(
          `${API_URL}/packages/editpackage/?package_id=${editPackageId}`,
          editPayload,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );
        showSnackbar(
          "Package updated successfully!",
          "success",
          "Please re-assign the form for the updated package.",
        );
      } else {
        const createPayload = {
          package_name: packageData.package_name,
          package_cost: Number(packageData.package_cost),
          duration: Number(packageData.duration),
          service_details: serviceDetails.map((s) => ({
            service_id: Number(s.service_id),
            sub_service_id: Number(s.sub_service_id),
            duration: Number(s.duration),
          })),
        };
        await axios.post(`${API_URL}/packages/create/`, createPayload, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });
        showSnackbar("Package created successfully!", "success");
      }
      handleModalClose();
      fetchPackages();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Something went wrong!",
        "error",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // -------------------------------------------------------------
  // CLOSURE BUILDER HANDLERS
  // -------------------------------------------------------------
  const handleClosureChange = (e) =>
    setClosureData({ ...closureData, [e.target.name]: e.target.value });

  const addQuestion = () =>
    setQuestions([
      ...questions,
      {
        Question: "",
        type: 1,
        type_options: [{ value: "", nestedQuestion: null }],
        key: "",
      },
    ]);

  const removeQuestion = (index) =>
    setQuestions(questions.filter((_, i) => i !== index));

  const handleQuestionChange = (index, field, value) => {
    const newQs = [...questions];
    newQs[index][field] = value;
    if (field === "type" && value === 3) newQs[index].type_options = [];
    else if (
      field === "type" &&
      value !== 3 &&
      newQs[index].type_options.length === 0
    )
      newQs[index].type_options = [{ value: "", nestedQuestion: null }];
    setQuestions(newQs);
  };

  const addOption = (qIndex) => {
    const newQs = [...questions];
    newQs[qIndex].type_options.push({ value: "", nestedQuestion: null });
    setQuestions(newQs);
  };

  const removeOption = (qIndex, optIndex) => {
    const newQs = [...questions];
    newQs[qIndex].type_options = newQs[qIndex].type_options.filter(
      (_, i) => i !== optIndex,
    );
    setQuestions(newQs);
  };

  const handleOptionChange = (qIndex, optIndex, value) => {
    const newQs = [...questions];
    newQs[qIndex].type_options[optIndex].value = value;
    setQuestions(newQs);
  };

  const toggleNestedQuestion = (qIndex, optIndex) => {
    const newQuestions = [...questions];
    const opt = newQuestions[qIndex].type_options[optIndex];

    if (opt.nestedQuestion) {
      opt.nestedQuestion = null;
    } else {
      opt.nestedQuestion = {
        Question: "",
        type: 1,
        type_options: [{ value: "" }, { value: "" }],
      };
    }
    setQuestions(newQuestions);
  };

  const handleNestedQuestionChange = (qIndex, optIndex, field, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].type_options[optIndex].nestedQuestion[field] = value;
    setQuestions(newQuestions);
  };

  const addNestedOption = (qIndex, optIndex) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].type_options[
      optIndex
    ].nestedQuestion.type_options.push({ value: "" });
    setQuestions(newQuestions);
  };

  const removeNestedOption = (qIndex, optIndex, nOptIndex) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].type_options[
      optIndex
    ].nestedQuestion.type_options.splice(nOptIndex, 1);
    setQuestions(newQuestions);
  };

  const handleNestedOptionChange = (qIndex, optIndex, nOptIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].type_options[optIndex].nestedQuestion.type_options[
      nOptIndex
    ].value = value;
    setQuestions(newQuestions);
  };

  const resetClosureForm = () => {
    setClosureData({ formName: "", shift: "" });
    setQuestions([
      {
        Question: "",
        type: 1,
        type_options: [{ value: "", nestedQuestion: null }],
        key: "",
      },
    ]);
    setOpenClosureModal(false);
  };

  const handleSaveClosureForm = async (payload) => {
    setSubmitLoading(true);
    try {
      await axios.post(`${API_URL}/packages/create_closure/`, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      showSnackbar("Closure Form created successfully!", "success");
      resetClosureForm();
      fetchAllClosureForms();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to create closure form",
        "error",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // -------------------------------------------------------------
  // PREVIEW & ASSIGN FORM HANDLERS
  // -------------------------------------------------------------
  const openPreviewAssignModal = (pkg) => {
    setSelectedPreviewPackage(pkg);
    setOpenAssignPreviewModal(true);
  };

  const handleAssignFormSubmit = async (pkgId, srvId, subSrvId, formId) => {
    if (!formId)
      return showSnackbar("Please select a form to assign", "warning");
    try {
      const response = await axios.post(
        `${API_URL}/packages/assign_form/?pkg_id=${pkgId}&srv_id=${srvId}&sub_srv_id=${subSrvId}&form_id=${formId}`,
        null,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      showSnackbar(
        response.data?.message || "Form successfully assigned!",
        "success",
      );

      const selectedForm = activeClosureForms.find((f) => f.form_id === formId);
      const formName = selectedForm ? selectedForm.form_name : "Assigned Form";

      setSelectedPreviewPackage((prev) => {
        if (!prev) return prev;
        const updatedServices = prev.service_details.map((srv) =>
          srv.service === srvId && srv.sub_service === subSrvId
            ? { ...srv, form_id: formId, closure_form_name: formName }
            : srv,
        );
        return { ...prev, service_details: updatedServices };
      });

      fetchPreviewPackages();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to assign form",
        "error",
      );
    }
  };

  const handleUnassignForm = async (pkgId, srvId, subSrvId) => {
    try {
      const response = await axios.put(
        `${API_URL}/packages/UnAssignForm/?pkg_id=${pkgId}&srv_id=${srvId}&sub_srv_id=${subSrvId}`,
        null,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      showSnackbar(
        response.data?.message || "Form unassigned successfully!",
        "success",
      );

      setSelectedPreviewPackage((prev) => {
        if (!prev) return prev;
        const updatedServices = prev.service_details.map((srv) =>
          srv.service === srvId && srv.sub_service === subSrvId
            ? { ...srv, form_id: null, closure_form_name: null }
            : srv,
        );
        return { ...prev, service_details: updatedServices };
      });

      fetchPreviewPackages();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to unassign form",
        "error",
      );
    }
  };

  // -------------------------------------------------------------
  // DYNAMIC TABLE DATA & HEADERS
  // -------------------------------------------------------------
  const tableHeaders = useMemo(() => {
    if (view === "packages")
      return ["Sr. No", "Package Name", "Cost (₹)", "Days", "Status", "Action"];
    if (view === "closure") return ["Sr. No", "Form Name", "Multiple Closures in a Day", "Status"];
    if (view === "preview")
      return [
        "Sr. No",
        "Package Name",
        "Cost (₹)",
        "Days",
        "Form Status",
        "Action",
      ];
  }, [view]);

  const filteredDataList = useMemo(() => {
    let list = [];
    if (view === "closure") list = allClosureForms;
    else if (view === "preview") list = previewPackages;
    else list = packages;

    if (!searchQuery) return list;

    const lowerQuery = searchQuery.toLowerCase();
    return list.filter((item) => {
      if (view === "packages" || view === "preview") {
        return item.package_name?.toLowerCase().includes(lowerQuery);
      }
      if (view === "closure") {
        const formName = item.form_name || item.name || "";
        return formName.toLowerCase().includes(lowerQuery);
      }
      return false;
    });
  }, [view, packages, allClosureForms, previewPackages, searchQuery]);

  const currentDataList =
    view === "closure"
      ? allClosureForms
      : view === "preview"
        ? previewPackages
        : packages;

  const paginatedData = useMemo(() => {
    const safeList = Array.isArray(filteredDataList) ? filteredDataList : [];
    return safeList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredDataList, page, rowsPerPage]);

  return (
    <>
      <HRNavbar />
      <Box sx={{ flexGrow: 1, mt: 14.6, ml: 1, mr: 1, mb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ pt: 1, px: 2, mb: 2 }}
        >
          {/* --- LEFT SIDE: Title & Search Bar --- */}
          <Stack direction="row" spacing={3} alignItems="center">
            <Typography
              style={{ fontSize: 18, fontWeight: 600 }}
              color="#347B89"
            >
              {view === "packages"
                ? "PACKAGE MANAGEMENT"
                : view === "closure"
                  ? "CLOSURE FORM MANAGEMENT"
                  : "PREVIEW & ASSIGN FORMS"}
            </Typography>

            <TextField
              size="small"
              variant="outlined"
              placeholder={`Search ${
                view === "packages"
                  ? "packages"
                  : view === "closure"
                    ? "forms"
                    : "assigned packages"
              }...`}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0); // Reset page to 0 when typing a new search
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 250,
                bgcolor: "white",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          </Stack>

          {/* --- RIGHT SIDE: Buttons & Tabs --- */}
          <Stack direction="row" spacing={3} alignItems="center">
            {view === "packages" && (
              <Button
                variant="contained"
                onClick={() => setOpenPackageModal(true)}
                sx={{ bgcolor: "#347B89", "&:hover": { bgcolor: "#205964" } }}
              >
                + Create Package
              </Button>
            )}
            {view === "closure" && (
              <Button
                variant="contained"
                onClick={() => setOpenClosureModal(true)}
                sx={{ bgcolor: "#388E3C", "&:hover": { bgcolor: "#2E7D32" } }}
              >
                + Create Form
              </Button>
            )}

            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, val) => {
                if (val) setView(val);
              }}
              size="small"
              sx={{ bgcolor: "#F0F0F0", borderRadius: "8px", p: "4px" }}
            >
              <ToggleButton
                value="packages"
                sx={{
                  border: "none",
                  fontWeight: 600,
                  "&.Mui-selected": { bgcolor: "#FFF", color: "#347B89" },
                }}
              >
                PACKAGES
              </ToggleButton>
              <ToggleButton
                value="closure"
                sx={{
                  border: "none",
                  fontWeight: 600,
                  "&.Mui-selected": { bgcolor: "#FFF", color: "#347B89" },
                }}
              >
                CLOSURE FORMS
              </ToggleButton>
              <ToggleButton
                value="preview"
                sx={{
                  border: "none",
                  fontWeight: 600,
                  "&.Mui-selected": { bgcolor: "#FFF", color: "#347B89" },
                }}
              >
                PREVIEW & ASSIGN FORMS
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
        <Fade in={true} timeout={500}>
          <Box>
            <TableContainer
              sx={{ height: "calc(100vh - 280px)", overflowY: "auto" }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <OngoingServiceCard
                      style={{
                        background: "#A5D6A7",
                        color: "#000",
                        borderRadius: "8px 10px 0 0",
                        marginTop: 0,
                        height: "3rem",
                      }}
                    >
                      {tableHeaders.map((head, i) => (
                        <CardContent
                          key={i}
                          style={{
                            flex: i === 0 ? 0.5 : 1,
                            borderRight: "1px solid #FFF",
                            padding: "8px 16px",
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={600}>
                            {head}
                          </Typography>
                        </CardContent>
                      ))}
                    </OngoingServiceCard>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <CardContent
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 5,
                        }}
                      >
                        <CircularProgress />
                      </CardContent>
                    </TableRow>
                  ) : currentDataList.length === 0 ? (
                    <TableRow>
                      <CardContent>
                        <Typography sx={{ p: 2, textAlign: "center" }}>
                          No Data Available
                        </Typography>
                      </CardContent>
                    </TableRow>
                  ) : (
                    paginatedData.map((row, index) => (
                      <TableRow
                        key={row.id || row.package_id || row.form_id || index}
                      >
                        <OngoingServiceCard
                          sx={{
                            borderRadius: "0px",
                            borderBottom: "1px solid #E0E0E0",
                          }}
                        >
                          <CardContent
                            style={{
                              flex: 0.5,
                              borderRight: "1px solid #E0E0E0",
                            }}
                          >
                            <Typography variant="body2">
                              {index + 1 + page * rowsPerPage}
                            </Typography>
                          </CardContent>

                          {/* --- PACKAGES VIEW --- */}
                          {view === "packages" && (
                            <>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {row.package_name}
                                </Typography>
                              </CardContent>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                <Typography variant="body2">
                                  ₹{row.package_cost}
                                </Typography>
                              </CardContent>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                <Typography variant="body2">
                                  {row.duration}
                                </Typography>
                              </CardContent>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  padding: "4px",
                                }}
                              >
                                <Switch
                                  checked={!!row.status}
                                  onChange={() =>
                                    handleTogglePackageStatus(row.package_id)
                                  }
                                  size="small"
                                  color="success"
                                />
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color={
                                    row.status ? "success.main" : "error.main"
                                  }
                                >
                                  {row.status ? "Active" : "Inactive"}
                                </Typography>
                              </CardContent>
                              <CardContent
                                style={{ flex: 1, textAlign: "center" }}
                              >
                                {(() => {
                                  const isEditable = row.IsEdit === true;

                                  return (
                                    <Tooltip
                                      title={
                                        isEditable
                                          ? "Edit Package"
                                          : "Package is not editable due to closure form assigned to this package"
                                      }
                                    >
                                      <span
                                        style={{
                                          cursor: isEditable
                                            ? "pointer"
                                            : "not-allowed",
                                        }}
                                      >
                                        <IconButton
                                          disabled={!isEditable}
                                          color="primary"
                                          onClick={() => handleEditClick(row)}
                                        >
                                          <EditOutlinedIcon />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  );
                                })()}
                              </CardContent>
                            </>
                          )}

                          {/* --- CLOSURE FORMS VIEW --- */}
                          {view === "closure" && (
                            <>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {row.form_name || row.name || "N/A"}
                                </Typography>
                              </CardContent>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                <Typography variant="body2">
                                  {row.shifts === 1
                                    ? "1 - One time"
                                    : row.shifts === 2
                                      ? "2 - Morning & Afternoon"
                                      : row.shifts === 3
                                        ? "3 - Morning, Afternoon & Evening"
                                        : row.shifts}
                                </Typography>
                              </CardContent>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  padding: "4px",
                                }}
                              >
                                <Switch
                                  checked={!!row.status}
                                  onChange={() =>
                                    handleToggleClosureStatus(row.form_id)
                                  }
                                  size="small"
                                  color="success"
                                />
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color={
                                    row.status ? "success.main" : "error.main"
                                  }
                                >
                                  {row.status ? "Active" : "Inactive"}
                                </Typography>
                              </CardContent>
                            </>
                          )}

                          {/* --- PREVIEW & ASSIGN VIEW --- */}
                          {view === "preview" && (
                            <>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {row.package_name}
                                </Typography>
                              </CardContent>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                <Typography variant="body2">
                                  ₹{row.package_cost}
                                </Typography>
                              </CardContent>
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                <Typography variant="body2">
                                  {row.duration}
                                </Typography>
                              </CardContent>

                              {/* Admin Indicator Column */}
                              <CardContent
                                style={{
                                  flex: 1,
                                  borderRight: "1px solid #E0E0E0",
                                }}
                              >
                                {row.service_details?.length > 0 &&
                                row.service_details.every(
                                  (s) => s.form_id != null,
                                ) ? (
                                  <Tooltip title="All services have assigned forms">
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "success.main",
                                        fontWeight: 700,
                                      }}
                                    >
                                      ● Assigned
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Some services are missing forms">
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "warning.main",
                                        fontWeight: 700,
                                      }}
                                    >
                                      ● Pending
                                    </Typography>
                                  </Tooltip>
                                )}
                              </CardContent>

                              <CardContent
                                style={{ flex: 1, textAlign: "center" }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<LinkIcon />}
                                  onClick={() => openPreviewAssignModal(row)}
                                  sx={{ borderRadius: "20px" }}
                                >
                                  View / Assign
                                </Button>
                              </CardContent>
                            </>
                          )}
                        </OngoingServiceCard>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              sx={{ overflowY: "hidden" }}
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={currentDataList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Box>
        </Fade>
      </Box>

      {/* --- MODAL FOR CREATE / EDIT PACKAGE --- */}
      <Modal open={openPackageModal} onClose={handleModalClose}>
        <Fade in={openPackageModal}>
          <Box sx={modalStyle}>
            <CreatePackage
              isEditMode={isEditMode}
              packageData={packageData}
              handlePackageChange={handlePackageChange}
              serviceDetails={serviceDetails}
              addServiceField={addServiceField}
              removeServiceField={removeServiceField}
              handleServiceChange={handleServiceChange}
              servicesList={servicesList}
              subServicesMap={subServicesMap}
              handleModalClose={handleModalClose}
              handleSavePackage={handleSavePackage}
              submitLoading={submitLoading}
            />
          </Box>
        </Fade>
      </Modal>

      {/* --- MODAL FOR CREATE CLOSURE FORM --- */}
      <Modal open={openClosureModal} onClose={resetClosureForm}>
        <Fade in={openClosureModal}>
          <Box sx={modalStyle}>
            <Closureformbuild
              closureData={closureData}
              handleClosureChange={handleClosureChange}
              questions={questions}
              addQuestion={addQuestion}
              removeQuestion={removeQuestion}
              handleQuestionChange={handleQuestionChange}
              addOption={addOption}
              removeOption={removeOption}
              handleOptionChange={handleOptionChange}
              toggleNestedQuestion={toggleNestedQuestion}
              handleNestedQuestionChange={handleNestedQuestionChange}
              addNestedOption={addNestedOption}
              removeNestedOption={removeNestedOption}
              handleNestedOptionChange={handleNestedOptionChange}
              handleModalClose={resetClosureForm}
              handleSaveClosureForm={handleSaveClosureForm}
              submitLoading={submitLoading}
            />
          </Box>
        </Fade>
      </Modal>

      {/* --- MODAL FOR PREVIEW & ASSIGN FORM --- */}
      <Modal
        open={openAssignPreviewModal}
        onClose={() => setOpenAssignPreviewModal(false)}
      >
        <Fade in={openAssignPreviewModal}>
          <Box sx={modalStyle}>
            <Stack
              direction="row"
              justifyContent="space-between"
              mb={2}
              alignItems="center"
            >
              <Typography variant="h6" color="#347B89" fontWeight={600}>
                Assign Forms: {selectedPreviewPackage?.package_name}
              </Typography>
              <Button
                onClick={() => setOpenAssignPreviewModal(false)}
                color="error"
              >
                Close
              </Button>
            </Stack>

            {selectedPreviewPackage && (
              <ServiceFormPreview
                packageData={selectedPreviewPackage}
                activeClosureForms={activeClosureForms} // Passed active forms
                handleAssignFormSubmit={handleAssignFormSubmit}
                handleUnassignForm={handleUnassignForm}
              />
            )}
          </Box>
        </Fade>
      </Modal>

      {/* --- CENTER-BOTTOM SNACKBAR --- */}
      <Snackbar
        key={snackbar.message}
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }} // Centered at the bottom
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Footer />
    </>
  );
};

export default PackageList;
